import React, {useCallback, useMemo} from 'react';
import {Text, Icon, Switch, TextInput, Status} from '@/ui-kits';
import {DataTableColumn} from '@/interface';
import {DataTable} from '@/components';
import {cn} from '@/utils/common_functions';

interface FilterItem {
  id: string;
  label: string;
  from: number | string;
  to: number | string;
  status: boolean;
  order?: number;
}

interface RatingFilterTableProps {
  data: FilterItem[];
  editingId: string | null;
  editForm: Partial<FilterItem>;
  onEdit: (item: FilterItem) => void;
  onSave: (id: string) => void;
  onCancel: () => void;
  onEditField: (field: string, value: any) => void;
  onStatusToggle: (id: string) => void;
  loading?: boolean;
}

export const RatingFilterTable: React.FC<RatingFilterTableProps> = ({
  data,
  editingId,
  editForm,
  onEdit,
  onSave,
  onCancel,
  onEditField,
  onStatusToggle,
  loading = false,
}) => {
  // ==============================
  // functions
  // ==============================
  const handleDecimalInput = useCallback((value: string): string => {
    if (value === '') return '';
    let cleaned = value.replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    return cleaned;
  }, []);

  // ==============================
  // data
  // ==============================
  const columns: DataTableColumn<FilterItem>[] = useMemo((): DataTableColumn<FilterItem>[] => {
    return [
      {
        name: 'label',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            Label
          </Text>
        ),
        render: row =>
          editingId === row.id ? (
            <TextInput
              value={String(editForm.label ?? row.label)}
              onChangeText={value => onEditField('label', value)}
              wrapperClassName="border-[1px] w-full max-w-[200px] py-0 rounded"
              className={cn('text-sm')}
              inputClassName="py-1 !font-FigtreeRegular text-sm"
              name="label"
            />
          ) : (
            <div className="flex items-center gap-2">
              <Text variant="body" className="!font-FigtreeSemiBold text-sm text-ui_input_border_dark">
                {row.label}
              </Text>
              <Icon type="star_yellow" className="size-4 flex-shrink-0" />
            </div>
          ),
        align: 'left',
        width: {minWidth: '50px', maxWidth: '50px'},
      },
      {
        name: 'from',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            From
          </Text>
        ),
        render: row =>
          editingId === row.id ? (
            <div className="flex justify-center">
              <TextInput
                type="text"
                value={String(editForm.from ?? row.from)}
                onChangeText={value => {
                  const cleanedValue = handleDecimalInput(value);
                  onEditField('from', cleanedValue);
                }}
                wrapperClassName="border-[1px] w-full max-w-[100px] py-0 rounded"
                className={cn('text-sm')}
                inputClassName="py-1 !font-FigtreeRegular text-sm text-center"
                name="from"
              />
            </div>
          ) : (
            <Text variant="body" className="!font-FigtreeSemiBold text-sm text-ui_input_border_dark">
              {row.from}
            </Text>
          ),
        align: 'center',
        width: {minWidth: '50px', maxWidth: '100px'},
      },
      {
        name: 'to',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            To
          </Text>
        ),
        render: row => {
          if (editingId === row.id) {
            const toValue = editForm.to !== undefined ? editForm.to : row.to;
            const displayValue = toValue === 'infinity' ? '' : String(toValue);
            return (
              <div className="flex justify-center">
                <TextInput
                  type="text"
                  value={displayValue}
                  onChangeText={value => {
                    if (value === '') {
                      onEditField('to', 'infinity');
                    } else {
                      const cleanedValue = handleDecimalInput(value);
                      onEditField('to', cleanedValue);
                    }
                  }}
                  wrapperClassName="border-[1px] w-full max-w-[100px] py-0 rounded"
                  className={cn('text-sm')}
                  inputClassName="py-1 !font-FigtreeRegular text-sm text-center"
                  name="to"
                  placeholder={row.to === 'infinity' ? 'infinity' : ''}
                />
              </div>
            );
          }
          return (
            <div className="flex items-center justify-center">
              {row.to === 'infinity' ? (
                <Icon type="infinity" className="size-5" />
              ) : (
                <Text variant="body" className="!font-FigtreeSemiBold text-sm text-ui_input_border_dark">
                  {row.to}
                </Text>
              )}
            </div>
          );
        },
        align: 'center',
        width: {minWidth: '50px', maxWidth: '100px'},
      },
      {
        name: 'status',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            Status
          </Text>
        ),
        render: row =>
          editingId === row.id ? (
            <div className="flex items-center justify-center">
              <div className="scale-125">
                <Switch value={editForm.status !== undefined ? editForm.status : row.status} onChange={checked => onEditField('status', checked)} />
              </div>
            </div>
          ) : (
            <Status status={row.status} variant='badge'/>
          ),
        align: 'center',
        width: {minWidth: '100px', maxWidth: '150px'},
      },
      {
        name: 'action',
        title: (
          <Text variant="subtitle" className="text-nowrap !font-FigtreeBold">
            Actions
          </Text>
        ),
        render: row =>
          editingId === row.id ? (
            <div className="flex justify-center w-full gap-4">
              <button className="flex justify-center" onClick={() => onSave(row.id)}>
                <Icon type="green_tick_btn" className="size-6" />
              </button>
              <button className="flex justify-center" onClick={onCancel}>
                <Icon type="cross_btn_red" className="size-6" />
              </button>
            </div>
          ) : (
            <div className="flex justify-center w-full gap-4">
              <button className="flex justify-center" onClick={() => onEdit(row)}>
                <Icon type="edit_input" className="size-4 self-center" />
              </button>
            </div>
          ),
        align: 'center',
        width: {minWidth: '70px', maxWidth: '50px'},
      },
    ];
  }, [editingId, editForm, handleDecimalInput, onEdit, onSave, onCancel, onEditField, onStatusToggle]);

  return (
    <DataTable
      columns={columns}
      data={data}
      totalPages={1}
      currentPage={1}
      totalResult={data.length}
      onPageChange={() => {}}
      showFooter={false}
      loading={loading}
    />
  );
};
